// Full-document render of the tempurpedic PillowsLandingPage: PageSkeleton
// wrapping the body in order (normal site chrome; pillows-landing page CSS via
// page_chrome):
//
// 1. The hero (rendered only when the page has a hero_image): width-1600
//    rendition inline background, the live badge (its image tag spanned two
//    source lines, never lexed, and printed literal template syntax in
//    production -- that literal markup is reproduced here), the conditional
//    desktop copy plus the two CTA elements, and the ALWAYS-rendered mobile
//    block (full-class title div, CTAs duplicated).
// 2. The hero_sidekick stream: every SidekickBlock is ALWAYS in the DOM;
//    `display-status--{status}` CSS-gates its visibility (a CSS contract --
//    do not convert to a conditional render).
// 3. The pillows_props stream -- a bound-block loop (NO w-block wrappers,
//    Fragment mode) through the Toolbox page map plus the six bespoke
//    entries. The featured/brand-message sections keep the `lazy-background`
//    class + `data-background-image` attribute VERBATIM: the shared
//    useLazyBackground IntersectionObserver hook loads the backgrounds;
//    dropping either silently kills them. Product data (prices, star ratings,
//    blocktrans review counts, per-product JSON-LD) arrives via the
//    block-id-keyed SSR side-channels.
import { Immutable } from "immer";
import React from "react";
import { SubBlockStarRating } from "tsi-common-react/src/components/SubBlockStarRating/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { useLazyBackground } from "tsi-common-react/src/utils/lazyBackground";

import { templates } from "@reactivated";
import type {
    BrandMessageBlock as BrandMessageBlockValue,
    CategoryCalloutBlock as CategoryCalloutBlockValue,
    CustomerReviewsBlock as CustomerReviewsBlockValue,
    FeaturedProdBlock as FeaturedProdBlockValue,
    PopularProductsBlock as PopularProductsBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.PillowsLandingPageTemplate;
type IPopularEntry = IProps["popular_products_ssr"][string][number];
type IFeaturedSSR = IProps["featured_prod_ssr"][string];
type IBrandSSR = IProps["brand_message_ssr"][string];
type IPillowsImage = IProps["category_callout_ssr"][string][number];
type ICustomerReview = IProps["customer_reviews_ssr"][string][number];

const Hero = (props: {
    hero: NonNullable<IProps["hero"]>;
}): React.JSX.Element => {
    const { hero } = props;
    const ctas = (
        <>
            {hero.link_copy && (
                <div className="product-list-hero__button">
                    <a href={hero.link_url} className="button" role="button">
                        {hero.link_copy}
                    </a>
                </div>
            )}
            {hero.shop_all_copy && (
                <a href={hero.shop_all_url}>
                    <span className="product-list-hero__shop-link">
                        {hero.shop_all_copy}
                    </span>
                </a>
            )}
        </>
    );
    return (
        <section
            className="hero product-list-hero product-list-hero--shop-pillows-hero l-full-width"
            style={
                hero.background_image_url
                    ? {
                          backgroundImage: `url(${hero.background_image_url})`,
                      }
                    : undefined
            }
        >
            <div
                className="product-list-hero__overlay product-list-hero__overlay--shop-pillows-hero"
                aria-hidden="true"
            ></div>
            <div className="l-capped-width product-list-hero__inner">
                {hero.has_badge && (
                    <div className="product-list-hero-badge">
                        <div className="product-list-hero-badge__image-container product-list-hero-badge__image-container--pillows">
                            <div
                                className={
                                    hero.badge_position
                                        ? `product-list-hero-badge__image-container-inner product-list-hero-badge__image-container-inner--${hero.badge_position}`
                                        : "product-list-hero-badge__image-container-inner"
                                }
                            >
                                {hero.badge_image_url && (
                                    <img
                                        alt={hero.badge_image_alt}
                                        src={hero.badge_image_url}
                                        className="product-list-hero-badge__image product-list-hero-badge__image--pillows responsive-img"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="product-list-hero__content product-list-hero__content--pillows">
                    {hero.pre_title && (
                        <h6 className="product-list-hero__category product-list-hero__category--pillows">
                            {hero.pre_title}
                        </h6>
                    )}
                    {hero.title_html && (
                        <div
                            className="product-list-hero__title product-list-hero__title--pillows rich-text"
                            dangerouslySetInnerHTML={{
                                __html: hero.title_html,
                            }}
                        />
                    )}
                    {hero.description && (
                        <p className="product-list-hero__description product-list-hero__description--pillows">
                            {hero.description}
                        </p>
                    )}
                    {ctas}
                </div>
                <div className="product-list-hero__content product-list-hero__content--pillows product-list-hero__content--pillows-mobile">
                    <h6 className="product-list-hero__category product-list-hero__category--pillows">
                        {hero.mobile_pre_title}
                    </h6>
                    <div
                        className="product-list-hero__title product-list-hero__title--pillows rich-text"
                        dangerouslySetInnerHTML={{
                            __html: hero.mobile_title_html,
                        }}
                    />
                    <p className="product-list-hero__description product-list-hero__description--pillows">
                        {hero.mobile_description}
                    </p>
                    {ctas}
                </div>
            </div>
        </section>
    );
};

const Sidekick = (props: {
    sidekick: IProps["sidekicks"][number];
}): React.JSX.Element => {
    const { sidekick } = props;
    return (
        <section className={`sidekick display-status--${sidekick.status}`}>
            <div className="l-capped-width">
                <div className="sidekick-inner">
                    <div className="sidekick__container">
                        <div className="sidekick-info sidekick-info--pillows-landing">
                            <div className="sidekick-info-col1">
                                <div className="sidekick-info__pre-title">
                                    {sidekick.pre_title}
                                </div>
                                <h2
                                    className="sidekick-info__subtitle"
                                    dangerouslySetInnerHTML={{
                                        __html: sidekick.headline_html,
                                    }}
                                />
                                <p className="sidekick-info__copy">
                                    {sidekick.description}
                                </p>
                                {sidekick.details_link_name && (
                                    <a href={sidekick.details_url}>
                                        <div className="sidekick-info__view-details">
                                            {sidekick.details_link_name}
                                        </div>
                                    </a>
                                )}
                            </div>
                            <div className="sidekick-info-col2">
                                <div className="sidekick-info__pre-title sidekick-info__pre-title--empty">
                                    {" "}
                                </div>
                                <div className="sidekick-info__price">
                                    {sidekick.starting_price && (
                                        <span className="sidekick-info__price--strike">
                                            ${sidekick.starting_price}
                                        </span>
                                    )}
                                    <span className="sidekick-info__price__actual">
                                        ${sidekick.retail_price}
                                    </span>
                                </div>
                                <div className="sidekick-info__button">
                                    <a
                                        href={sidekick.button_url}
                                        role="button"
                                        className="button"
                                    >
                                        {sidekick.button_link_name}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="sidekick__main-image-container">
                            <img
                                className="sidekick__main-image sidekick__main-image--pillows-landing"
                                src={sidekick.image_url}
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const PopularProductCard = (props: {
    product: NonNullable<
        Immutable<PopularProductsBlockValue["products"]>
    >[number];
    entry: IPopularEntry | undefined;
}): React.JSX.Element => {
    const { product, entry } = props;
    const shopURL = product.shop_link?.url ?? "";
    return (
        <div className="card product-card product-list__card product-list__card--pillows">
            <div className="product-list__card__category product-list__card__category--pillows">
                <a href={shopURL}>
                    <p> {product.tagline}</p>
                </a>
            </div>
            <a href={shopURL} title={entry?.learn_more_title ?? ""}>
                {entry?.image_url && (
                    <img
                        src={entry.image_url}
                        className="card__image product-list__card__image responsive-img"
                        alt={entry.image_alt}
                        loading="lazy"
                        decoding="async"
                    />
                )}
            </a>
            <div className="card__content product-list__card__content">
                {product.pre_title ? (
                    <div className="product-list__card__highlight product-list__card__highlight--pillows">
                        <a href={shopURL}>{product.pre_title}</a>
                    </div>
                ) : (
                    <div className="product-list__card__highlight product-list__card__highlight--pillows product-list__card__highlight--pillows-transparent">
                        {" "}
                    </div>
                )}
                <h4>
                    <a href={shopURL}>{product.title}</a>
                </h4>
                {entry?.show_rating && (
                    <a
                        href={entry.reviews_href}
                        title={entry.read_reviews_label}
                    >
                        <div className="star-rating product-card__rating product-card__rating--pillows">
                            <SubBlockStarRating
                                rating={entry.rating}
                                number={null}
                            />
                        </div>
                        <div className="star-rating__number product-card__rating-number">
                            {entry.review_count_text}
                        </div>
                    </a>
                )}
                {entry?.json_ld && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: entry.json_ld }}
                    />
                )}
                <div className="product-card__aux-copy product-list__card__description product-list__card__description--pillows">
                    <p className="product-list__card__description--pillows-paragraph">
                        <a href={shopURL}>{product.description}</a>
                    </p>
                </div>
                {entry?.price && (
                    <div className="product-card__price product-list__card__price-elem">
                        {!entry.price.retail ? (
                            <span className="product-card__price__actual">
                                <a href={shopURL}>{entry.price.actual}</a>
                            </span>
                        ) : (
                            <>
                                <span className="product-card__price__retail">
                                    <span className="ada-screenreader-only">
                                        {entry.price.original_price_label}
                                    </span>
                                    <a href={shopURL}>{entry.price.retail}</a>
                                </span>
                                <span className="product-card__price__actual--highlight">
                                    <span className="ada-screenreader-only">
                                        {entry.price.current_price_label}
                                    </span>
                                    <a href={shopURL}>{entry.price.actual}</a>
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const PopularProducts = (props: {
    value: Immutable<PopularProductsBlockValue>;
    entries: readonly IPopularEntry[];
}): React.JSX.Element => {
    const { value, entries } = props;
    return (
        <section className="l-capped-width product-list product-list--pillows">
            <header className="product-list__header">
                <div
                    className="product-list__header-copy"
                    dangerouslySetInnerHTML={{
                        __html: value.headline ?? "",
                    }}
                />
            </header>
            <div className="product-list__content u-flex-container">
                {(value.products ?? []).map((product, i) => (
                    <PopularProductCard
                        key={i}
                        product={product}
                        entry={entries.at(i)}
                    />
                ))}
            </div>
            <div className="product-list__pillows-button">
                <a
                    href={value.shop_link?.url ?? ""}
                    className="button"
                    role="button"
                >
                    {value.shop_link_name}
                </a>
            </div>
        </section>
    );
};

const FeaturedProdContents = (props: {
    value: Immutable<FeaturedProdBlockValue>;
    ssr: IFeaturedSSR | undefined;
    reviewsHref: string;
    priceActualClass: string;
    withJsonLd: boolean;
}): React.JSX.Element => {
    const { value, ssr } = props;
    return (
        <div className="featured-product__contents">
            <div
                className="featured-product__tagline"
                dangerouslySetInnerHTML={{ __html: value.tagline ?? "" }}
            />
            <div
                className="featured-product__headline"
                dangerouslySetInnerHTML={{ __html: value.headline ?? "" }}
            />
            {value.sub_headline && (
                <div className="featured-product__sub_headline">
                    {value.sub_headline}
                </div>
            )}
            <a href={props.reviewsHref}>
                <div className="featured-product__rating">
                    <SubBlockStarRating
                        rating={ssr?.rating ?? 0}
                        number={null}
                    />
                </div>
                <div className="featured-product__review-number">
                    {ssr?.review_count_text ?? ""}
                </div>
            </a>
            <div
                className="featured-product__description"
                dangerouslySetInnerHTML={{
                    __html: value.product_description ?? "",
                }}
            />
            {ssr?.has_price && (
                <div className="featured-product__price">
                    {!ssr.price_retail ? (
                        <div
                            className={`featured-product__price--${props.priceActualClass}`}
                        >
                            {ssr.price_actual}
                        </div>
                    ) : (
                        <>
                            <div className="featured-product__price--starting-price">
                                {ssr.starting_at_label}{" "}
                                {/* The obsolete <strike> tag is preserved
                                    byte-exact (via createElement, since it's
                                    not a typed JSX intrinsic). */}
                                {React.createElement(
                                    "strike",
                                    null,
                                    ssr.price_retail,
                                )}
                            </div>
                            <div
                                className={`featured-product__price--${props.priceActualClass} featured-product__price--highlight`}
                            >
                                {ssr.price_actual}
                            </div>
                        </>
                    )}
                </div>
            )}
            <div className="featured-product__button">
                <a
                    href={value.shop_button?.url ?? ""}
                    className="button"
                    role="button"
                >
                    {value.shop_button_name}
                </a>
            </div>
            {props.withJsonLd && ssr?.json_ld && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: ssr.json_ld }}
                />
            )}
        </div>
    );
};

const FeaturedProd = (props: {
    value: Immutable<FeaturedProdBlockValue>;
    ssr: IFeaturedSSR | undefined;
}): React.JSX.Element => {
    const { value, ssr } = props;
    // Defer the two CSS background images (desktop section + mobile block) until
    // they scroll into view (eager under puppeteer/SSR) via the shared hook.
    const desktopRef = React.useRef<HTMLElement | null>(null);
    useLazyBackground(desktopRef);
    const mobileImageRef = React.useRef<HTMLDivElement | null>(null);
    useLazyBackground(mobileImageRef);
    return (
        <>
            <section
                ref={desktopRef}
                className="l-full-width featured-product lazy-background"
                data-background-image={ssr?.desktop_image_url ?? ""}
                role="img"
                aria-label={ssr?.image_title ?? ""}
            >
                <div className="l-capped-width featured-product__container">
                    <FeaturedProdContents
                        value={value}
                        ssr={ssr}
                        reviewsHref={ssr?.desktop_reviews_href ?? ""}
                        priceActualClass="actual"
                        withJsonLd={true}
                    />
                </div>
            </section>
            <section className="l-full-width featured-product featured-product--mobile">
                <div className="l-capped-width featured-product__container">
                    <div
                        ref={mobileImageRef}
                        className="featured-product__mobile-image lazy-background"
                        data-background-image={ssr?.mobile_image_url ?? ""}
                        role="img"
                        aria-label={ssr?.image_title ?? ""}
                    ></div>
                    {/* The mobile price divs use the misspelled `--actuall`
                        modifier intentionally -- do not fix the typo. */}
                    <FeaturedProdContents
                        value={value}
                        ssr={ssr}
                        reviewsHref={ssr?.mobile_reviews_href ?? ""}
                        priceActualClass="actuall"
                        withJsonLd={false}
                    />
                </div>
            </section>
        </>
    );
};

const CustomerReviews = (props: {
    value: Immutable<CustomerReviewsBlockValue>;
    items: readonly ICustomerReview[];
}): React.JSX.Element => {
    const { value, items } = props;
    return (
        <div className="section-testimonials l-capped-width">
            <h2>{value.headline}</h2>
            <div className="section-testimonials__body">
                {items.map((item, i) => (
                    <div
                        key={i}
                        className="section-testimonials__body-testimonial"
                    >
                        <div className="section-testimonials__body-testimonial__copy">
                            {item.text}
                        </div>
                        <SubBlockStarRating
                            rating={item.rating ?? 0}
                            number={null}
                            className=""
                        />
                        <div
                            className="section-testimonials__body-testimonial__author"
                            dangerouslySetInnerHTML={{
                                __html: item.author_html,
                            }}
                        />
                    </div>
                ))}
            </div>
            <footer className="section-testimonials__foot">
                <a href={value.review_link?.url ?? ""} className="button">
                    {value.review_link_name}
                </a>
            </footer>
        </div>
    );
};

const BrandMessage = (props: {
    value: Immutable<BrandMessageBlockValue>;
    ssr: IBrandSSR | undefined;
}): React.JSX.Element => {
    const { value, ssr } = props;
    // Defer the section's CSS background image until it scrolls into view (eager
    // under puppeteer/SSR) via the shared per-element hook.
    const sectionRef = React.useRef<HTMLElement | null>(null);
    useLazyBackground(sectionRef);
    return (
        <section
            ref={sectionRef}
            className="l-full-width brand-message lazy-background"
            data-background-image={ssr?.background_image_url ?? ""}
            role="img"
            aria-label={ssr?.image_title ?? ""}
        >
            <div className="l-capped-width brand-message__inner">
                <h2 className="brand-message__headline">{value.headline}</h2>
                <div
                    className="brand-message__copy"
                    dangerouslySetInnerHTML={{ __html: value.copy ?? "" }}
                />
                <div className="value-props-bar-block value-props-bar-block--pillows l-max-width">
                    <ul>
                        {(value.value_props ?? []).map((item, i) => {
                            const image = ssr?.value_prop_images.at(i);
                            return (
                                <li key={i}>
                                    <div>
                                        {image?.url && (
                                            <img
                                                alt={image.alt}
                                                src={image.url}
                                                className=""
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        )}
                                        <div className="value-prop-list-item__copy value-prop-list-item__copy--pillows-brand-message">
                                            <h5
                                                className="value-prop-list-item__title value-prop-list-item__title--pillows-brand-message u-text-uppercase "
                                                dangerouslySetInnerHTML={{
                                                    __html: item.title ?? "",
                                                }}
                                            />
                                            <p
                                                className="value-prop-list-item__subtitle value-prop-list-item__subtitle--pillows-brand-message"
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        item.description ?? "",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </section>
    );
};

const CategoryCallout = (props: {
    value: Immutable<CategoryCalloutBlockValue>;
    images: readonly IPillowsImage[];
}): React.JSX.Element => {
    const { value, images } = props;
    return (
        <section className="category-callout l-capped-width u-margin-bottom--xl">
            <div className="category-callout__copy">
                <h3 className="category-callout__title category-callout__title--pillows">
                    {value.headline}
                </h3>
                <div className="category-callout__description">
                    {value.description}
                </div>
            </div>
            <ul className="category-callout-list category-callout-list--pillows">
                {(value.category_list ?? []).map((item, i) => {
                    const linkURL = item.link?.url ?? "";
                    const image = images.at(i);
                    return (
                        <li
                            key={i}
                            className="category-callout-list__item category-callout-list__item--pillows"
                        >
                            <div className="category-callout-list__image-container category-callout-list__image-container--pillows">
                                <a href={linkURL}>
                                    {image?.url && (
                                        <img
                                            src={image.url}
                                            className="category-callout-list__image responsive-img"
                                            alt={image.alt}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    )}
                                </a>
                            </div>
                            <a href={linkURL}>
                                <h4>{item.copy}</h4>
                            </a>
                        </li>
                    );
                })}
            </ul>
            <a href={value.shop_link?.url ?? ""} className="button">
                {value.shop_link_name}
            </a>
        </section>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const toolboxMap = buildToolboxPageMap(props);
    const pillowsPropsMap = {
        ...toolboxMap,
        // The page's own key for the shared ValuePropBarBlock (distinct from
        // Toolbox's `value_prop_bar_block`, same component).
        value_prop_block: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,
        popular_products_block: (block: {
            id?: string;
            value: Immutable<PopularProductsBlockValue>;
        }) => (
            <PopularProducts
                value={block.value}
                entries={
                    (block.id
                        ? props.popular_products_ssr[block.id]
                        : undefined) ?? []
                }
            />
        ),
        featured_prod_block: (block: {
            id?: string;
            value: Immutable<FeaturedProdBlockValue>;
        }) => (
            <FeaturedProd
                value={block.value}
                ssr={block.id ? props.featured_prod_ssr[block.id] : undefined}
            />
        ),
        customer_reviews_block: (block: {
            id?: string;
            value: Immutable<CustomerReviewsBlockValue>;
        }) => (
            <CustomerReviews
                value={block.value}
                items={
                    (block.id
                        ? props.customer_reviews_ssr[block.id]
                        : undefined) ?? []
                }
            />
        ),
        brand_message_block: (block: {
            id?: string;
            value: Immutable<BrandMessageBlockValue>;
        }) => (
            <BrandMessage
                value={block.value}
                ssr={block.id ? props.brand_message_ssr[block.id] : undefined}
            />
        ),
        category_callout_block: (block: {
            id?: string;
            value: Immutable<CategoryCalloutBlockValue>;
        }) => (
            <CategoryCallout
                value={block.value}
                images={
                    (block.id
                        ? props.category_callout_ssr[block.id]
                        : undefined) ?? []
                }
            />
        ),
    };
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {props.hero && <Hero hero={props.hero} />}
            {props.sidekicks.map((sidekick, i) => (
                <Sidekick key={i} sidekick={sidekick} />
            ))}
            {props.page.pillows_props && (
                <StreamField
                    tagName={React.Fragment}
                    value={props.page.pillows_props.value}
                    components={pillowsPropsMap}
                />
            )}
        </PageSkeleton>
    );
};
